import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Modal from "react-modal";
import { ROUTES } from "../config/routes";
import { PromotionGroupsAdminService } from "../api/generated/services/PromotionGroupsAdminService";
import { PromotionGroupViewModel } from "../api/generated/models/PromotionGroupViewModel";
import { PromotionGroupType } from "../api/generated/models/PromotionGroupType";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { ConfirmationModal } from "../components/ConfirmationModal";
import {
  fetchBusinessesByIds,
  searchBusinesses,
  TypesenseBusinessDocument,
} from "../utils/typesenseBusinessSearch";
import "../api/openApiConfig";

interface FormData {
  title: string;
  subtitle: string;
  isPublished: boolean;
  visibilityStartAt: string;
  visibilityEndAt: string;
}

const toDatetimeLocal = (value: any): string => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    // Format as YYYY-MM-DDTHH:mm
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

interface SortableBusinessItemProps {
  business: TypesenseBusinessDocument;
  onRemove: (id: string) => void;
  canRemove: boolean;
}

const SortableBusinessItem: React.FC<SortableBusinessItemProps> = ({
  business,
  onRemove,
  canRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: business.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const logoUrl = business.logo_url?.startsWith("//")
    ? `https:${business.logo_url}`
    : business.logo_url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 mb-2"
    >
      {canRemove && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
          </svg>
        </div>
      )}

      {logoUrl ? (
        <img
          src={logoUrl}
          alt={business.business_name}
          className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
          <span className="text-gray-400 text-[8px]">No logo</span>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900 truncate">
          {business.business_name}
        </p>
        {business.cashback_display && (
          <p className="text-xs text-gray-500">{business.cashback_display}</p>
        )}
      </div>

      {canRemove && (
        <Button
          variant="icon"
          size="sm"
          onClick={() => onRemove(business.id)}
          className="flex-shrink-0"
        >
          <svg
            className="w-4 h-4 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      )}
    </div>
  );
};

const AdminFeaturedPartnerEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [group, setGroup] = useState<PromotionGroupViewModel | null>(null);
  const [businesses, setBusinesses] = useState<TypesenseBusinessDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Search modal state
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    TypesenseBusinessDocument[]
  >([]);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormData>();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const featured = group?.groupType === PromotionGroupType.FEATURED;

  const loadGroup = useCallback(async () => {
    if (!id) return;
    try {
      const data =
        await PromotionGroupsAdminService.getApiPromotionGroupsAdmin1(id);
      setGroup(data);

      reset({
        title: data.title || "",
        subtitle: data.subtitle || "",
        isPublished: data.isPublished || false,
        visibilityStartAt: toDatetimeLocal(data.visibilityStartAt),
        visibilityEndAt: toDatetimeLocal(data.visibilityEndAt),
      });

      if (data.businessIds && data.businessIds.length > 0) {
        const docs = await fetchBusinessesByIds(data.businessIds);
        setBusinesses(docs);
      }
    } catch (error) {
      console.error("Error loading group:", error);
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  const onSubmit = async (formData: FormData) => {
    if (!id) return;
    setSaving(true);
    try {
      const updated = await PromotionGroupsAdminService.putApiPromotionGroupsAdmin(
        id,
        {
          id,
          title: formData.title,
          subtitle: formData.subtitle,
          isPublished: formData.isPublished,
          visibilityStartAt: formData.visibilityStartAt
            ? (new Date(formData.visibilityStartAt).toISOString() as any)
            : undefined,
          visibilityEndAt: formData.visibilityEndAt
            ? (new Date(formData.visibilityEndAt).toISOString() as any)
            : undefined,
        }
      );
      setGroup(updated);
      reset({
        title: updated.title || "",
        subtitle: updated.subtitle || "",
        isPublished: updated.isPublished || false,
        visibilityStartAt: toDatetimeLocal(updated.visibilityStartAt),
        visibilityEndAt: toDatetimeLocal(updated.visibilityEndAt),
      });
    } catch (error) {
      console.error("Error saving group:", error);
    } finally {
      setSaving(false);
    }
  };

  const updateBusinessIds = async (newIds: string[]) => {
    if (!id) return;
    try {
      const updated =
        await PromotionGroupsAdminService.putApiPromotionGroupsAdminBusinesses(
          id,
          { id, businessIds: newIds }
        );
      setGroup(updated);
    } catch (error) {
      console.error("Error updating businesses:", error);
    }
  };

  const handleBusinessDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = businesses.findIndex((b) => b.id === active.id);
    const newIndex = businesses.findIndex((b) => b.id === over.id);
    const reordered = arrayMove(businesses, oldIndex, newIndex);
    setBusinesses(reordered);
    await updateBusinessIds(reordered.map((b) => b.id));
  };

  const handleRemoveBusiness = async (businessId: string) => {
    const updated = businesses.filter((b) => b.id !== businessId);
    setBusinesses(updated);
    await updateBusinessIds(updated.map((b) => b.id));
  };

  const handleAddBusiness = async (business: TypesenseBusinessDocument) => {
    const updated = [...businesses, business];
    setBusinesses(updated);
    await updateBusinessIds(updated.map((b) => b.id));
  };

  // Debounced search
  useEffect(() => {
    if (!searchModalOpen) return;

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { results } = await searchBusinesses(searchQuery, 1, 30);
        // Filter out already-added businesses
        const existingIds = new Set(businesses.map((b) => b.id));
        setSearchResults(results.filter((r) => !existingIds.has(r.id)));
      } catch (error) {
        console.error("Error searching businesses:", error);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery, searchModalOpen, businesses]);

  const openSearchModal = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSearchModalOpen(true);
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await PromotionGroupsAdminService.deleteApiPromotionGroupsAdmin(id);
      navigate(ROUTES.ADMIN_FEATURED_PARTNERS());
    } catch (error) {
      console.error("Error deleting group:", error);
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = group && !featured;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="text-center py-12 text-gray-500">Group not found.</div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(ROUTES.ADMIN_FEATURED_PARTNERS())}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Featured Partners
        </button>
        <div className="flex items-center justify-between">
          <h2 className="font-medium">Edit Group</h2>
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Group
            </Button>
          )}
        </div>
      </div>

      {/* Details Form — hidden for Featured group */}
      {!featured && (
        <form onSubmit={handleSubmit(onSubmit)} className="mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-medium text-gray-900 mb-4">Group Details</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  {...register("title")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle
                </label>
                <input
                  type="text"
                  {...register("subtitle")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visibility Start
                </label>
                <input
                  type="datetime-local"
                  {...register("visibilityStartAt")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visibility End
                </label>
                <input
                  type="datetime-local"
                  {...register("visibilityEndAt")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  {...register("isPublished")}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
              </label>
              <span className="text-sm font-medium text-gray-700">
                Active
              </span>
            </div>

            <div className="mt-6 flex justify-end">
              <Button type="submit" isLoading={saving} disabled={!isDirty}>
                Save Changes
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* Businesses Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-gray-900">Businesses</h3>
          <Button variant="outline" size="sm" onClick={openSearchModal}>
            Add Business
          </Button>
        </div>

        {businesses.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleBusinessDragEnd}
          >
            <SortableContext
              items={businesses.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              {businesses.map((biz) => (
                <SortableBusinessItem
                  key={biz.id}
                  business={biz}
                  onRemove={handleRemoveBusiness}
                  canRemove={true}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <p className="text-sm text-gray-400 italic">
            No businesses added yet. Click "Add Business" to start.
          </p>
        )}
      </div>

      {/* Search Modal */}
      <Modal
        className="modal-content md:max-w-[600px]"
        overlayClassName="modal-overlay"
        isOpen={searchModalOpen}
        onRequestClose={() => setSearchModalOpen(false)}
        contentLabel="Add Business"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-primary font-medium text-xl">Add Business</h2>
          <Button
            variant="icon"
            onClick={() => setSearchModalOpen(false)}
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        </div>

        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search for businesses..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-4"
          autoFocus
        />

        <div className="max-h-[400px] overflow-y-auto">
          {searching ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map((result) => {
              const logoUrl = result.logo_url?.startsWith("//")
                ? `https:${result.logo_url}`
                : result.logo_url;
              return (
                <button
                  key={result.id}
                  onClick={() => {
                    handleAddBusiness(result);
                    // Remove from search results
                    setSearchResults((prev) =>
                      prev.filter((r) => r.id !== result.id)
                    );
                  }}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={result.business_name}
                      className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-400 text-[8px]">No logo</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 truncate">
                      {result.business_name}
                    </p>
                    <div className="flex items-center gap-2">
                      {result.cashback_display && (
                        <span className="text-xs text-green-600">
                          {result.cashback_display}
                        </span>
                      )}
                      {result.partner_type && (
                        <span className="text-xs text-gray-400">
                          {result.partner_type}
                        </span>
                      )}
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-gray-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </button>
              );
            })
          ) : searchQuery ? (
            <p className="text-center py-8 text-gray-500 text-sm">
              No results found
            </p>
          ) : (
            <p className="text-center py-8 text-gray-400 text-sm">
              Start typing to search for businesses
            </p>
          )}
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Group"
        message={`Are you sure you want to delete "${group?.title || "this group"}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default AdminFeaturedPartnerEdit;
